/* ══════════════════════════════════════════════════════════════
   News Feed Service — Fetches from NewsData.io + RSS fallback
   Primary: NewsData.io API (live Indian business news)
   Fallback: RSS proxy (multiple Indian financial RSS feeds)
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from './api-client';

export interface NewsItem {
    title: string;
    link: string;
    description: string;
    pubDate: string;
    source: string;
    sourceIcon?: string;
    category: string;
    timeAgo: string;
    imageUrl?: string;
    sentiment?: string;
}

/**
 * Fetch news from NewsData.io API (primary source)
 */
async function fetchFromNewsDataAPI(
    category: string = 'business',
    size: number = 10,
    cacheTTL: number = 300
): Promise<NewsItem[]> {
    const result = await apiFetch<{
        articles: Array<{
            id: string;
            title: string;
            link: string;
            description: string;
            source: string;
            sourceIcon: string;
            category: string;
            pubDate: string;
            timeAgo: string;
            imageUrl: string;
            sentiment: string;
        }>;
    }>('/news', {
        cacheTTL,
        params: { category, size: String(size) },
    });

    return (result.data.articles || []).map(a => ({
        title: a.title,
        link: a.link,
        description: a.description,
        pubDate: a.pubDate,
        source: a.source,
        sourceIcon: a.sourceIcon,
        category: a.category,
        timeAgo: a.timeAgo,
        imageUrl: a.imageUrl,
        sentiment: a.sentiment,
    }));
}

/**
 * Fetch news from RSS proxy (fallback)
 */
async function fetchFromRSS(maxFeeds: number = 6, cacheTTL: number = 300): Promise<NewsItem[]> {
    try {
        const { INDIA_FEEDS } = await import('../config/india-feeds');
        const { deduplicateHeadlines } = await import('../utils/dedup');

        const feeds = INDIA_FEEDS
            .filter(f => f.language === 'en')
            .sort((a, b) => a.tier - b.tier)
            .slice(0, maxFeeds);

        const results = await Promise.allSettled(
            feeds.map(f =>
                apiFetch<{ items: NewsItem[] }>('/rss-proxy', {
                    cacheTTL,
                    params: { url: f.url, source: f.name },
                }).then(r => (r.data.items || []).map(item => ({
                    ...item,
                    source: f.name,
                    timeAgo: getTimeAgo(item.pubDate),
                })))
            )
        );

        let allItems: NewsItem[] = [];
        for (const result of results) {
            if (result.status === 'fulfilled') allItems.push(...result.value);
        }

        allItems.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
        allItems = deduplicateHeadlines(allItems);
        return allItems;
    } catch {
        return [];
    }
}

/**
 * Fetch news — tries NewsData.io API first, falls back to RSS
 */
export async function fetchNews(options: {
    maxFeeds?: number;
    category?: string;
    language?: string;
    size?: number;
    cacheTTL?: number;
} = {}): Promise<NewsItem[]> {
    const { category = 'business', size = 10, cacheTTL = 300 } = options;

    try {
        // Try NewsData.io API first (better quality, real-time)
        const articles = await fetchFromNewsDataAPI(category, size, cacheTTL);
        if (articles.length > 0) return articles;
    } catch {
        // fall through to RSS
    }

    // Fallback to RSS feeds
    return fetchFromRSS(options.maxFeeds || 6, cacheTTL);
}

/**
 * Shorthand for fetching priority headlines (top 10)
 */
export async function fetchPriorityHeadlines(): Promise<NewsItem[]> {
    const news = await fetchNews({ category: 'business' });
    return news.slice(0, 10);
}

function getTimeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${Math.floor(diffHrs / 24)}d ago`;
}
